/**
 * Risk Disclosure & Warnings — the single source of the text clients legally agree to.
 *
 * WHY THIS FILE EXISTS. This copy previously lived hardcoded and duplicated inside the
 * modals that render it, which made two things impossible:
 *   1. Chris's instruction on the 2026-08-13 revision — "bump the disclosure document
 *      version when publishing" — had nothing to bump; there was no version anywhere.
 *   2. Recording WHICH text a client agreed to. A consent record is worth little if the
 *      wording it refers to can drift underneath it, and four copies drift.
 *
 * So: the words live here once, every surface renders from here, and the version below
 * identifies exactly what was on screen when someone ticked the box.
 *
 * ── HOW TO PUBLISH A CHANGE ────────────────────────────────────────────────────────
 * Change the text, then bump DISCLOSURE_VERSION in the same commit. Never edit wording
 * without bumping: consent rows already written keep pointing at the old version, which
 * is the whole point. The version is the publication date because "which text did this
 * client agree to" is a question asked by date, not by semver.
 *
 * ── SEGMENTS ───────────────────────────────────────────────────────────────────────
 * A list item is either a plain string, or an array of segments where `{ em: '...' }`
 * renders inside <strong>. That exists only to preserve the emphasis already live on the
 * warnings page word-for-word — it is not a formatting system, and it deliberately is
 * not HTML, so nothing here can inject markup.
 *
 * ── SCOPE NOTE ─────────────────────────────────────────────────────────────────────
 * Two other components carry disclosure-shaped text and are intentionally NOT sourced
 * from here, because the 2026-08-13 revision does not cover them:
 *   - RiskDisclosureModal.jsx — dead code (zero references); still holds the pre-revision
 *     wording, including brokers we do not offer. Delete or revive it deliberately.
 *   - DisclaimerModal.jsx — live via RightColumn, but it is a DIFFERENT, older document
 *     ("Automated Trading Disclaimer", "JTNCBot" branding, ellipsised body).
 * Both are flagged rather than quietly rewritten; changing legal text nobody asked for
 * is worse than leaving it visible.
 */

/**
 * Publication date of the wording below. Bump on ANY wording change.
 * 2026-08-14 — first versioned publication; carries Chris's 2026-08-13 revision:
 *   • symbol wording widened from "the ETF (SPY, SPX, or QQQ)" to the offered set
 *   • autonomous-execution clause now names Standing Trading Authorization
 *   • broker list corrected to Tradier and Tastytrade (previously named Schwab and
 *     Interactive Brokers, which are not offered to clients)
 *   • warnings-page acknowledgment gains the Standing Trading Authorization item
 */
export const DISCLOSURE_VERSION = '2026-08-14';

/**
 * Shown in the engagement flow, after the client clicks Engage.
 * Rendered with each item as its own paragraph — matching how it renders today.
 */
export const RISK_DISCLOSURE = {
  title: 'Risk Disclosure & Waiver of Liability',
  warning:
    'Algorithmic trading is inherently risky. Do not proceed unless you fully understand and accept the risks outlined below. By continuing, you confirm that you have read, understood, and voluntarily accept full responsibility for any and all outcomes resulting from your use of JTNC’s Quantum Flo Auto Algo.',
  sections: [
    {
      heading: 'General Trading Risk',
      lead: 'Trading financial markets, particularly using automated systems, carries significant risk of loss. The use of algorithms does not guarantee profits, and past performance is not indicative of future results. By engaging JTNC’s Quantum Flo Auto Algo, you acknowledge that:',
      items: [
        'You may lose some or all of your capital.',
        'You assume full responsibility for monitoring your account.',
        'No level of automation can eliminate market risk, volatility, or black swan events.',
      ],
    },
    {
      heading: 'User Responsibility for Configuration',
      lead: 'You are solely responsible for configuring JTNC’s Quantum Flo Auto Algo, including:',
      items: [
        'Choosing the symbol(s) to trade (SPY, QQQ, and/or SPX, as offered in the engagement flow).',
        'Setting contract limits, account size parameters, and any broker-level settings.',
        'Ensuring your brokerage account is properly approved and funded.',
      ],
      closing: [
        'JTNC Group, LLC. assumes no liability for misconfigured settings, input errors, or inappropriate use of the system based on your financial situation or account structure.',
      ],
    },
    {
      heading: 'Understanding Algorithm Behavior',
      lead: 'By proceeding, you acknowledge that:',
      items: [
        'You understand how JTNC’s Quantum Flo Auto Algo enters and exits trades, including its reliance on market range forecasting, options flow, and sentiment modeling.',
        'You are aware that JTNC’s Quantum Flo Auto Algo will autonomously execute trades in your account without additional manual confirmation (after your daily opt-in, or during any period covered by a Standing Trading Authorization you have signed).',
        'You retain full control to pause, disable, or override trades at any time via your brokerage interface.',
      ],
    },
    {
      heading: 'Market Risk & Volatility',
      lead: 'JTNC’s Quantum Flo Auto Algo does not provide protection from:',
      items: [
        'Sudden market volatility or flash crashes.',
        'Overnight gaps (though trades are typically not held overnight or over weekends).',
        'Macro events, geopolitical shocks, or unexpected news.',
      ],
      closing: [
        'All trades are subject to real-time market dynamics, including slippage and price dislocation.',
      ],
    },
    {
      heading: 'Third-Party Broker & Data Integration',
      lead: 'JTNC’s Quantum Flo Auto Algo connects to your brokerage account via authorized API access. As such:',
      items: [
        'JTNC Group, LLC is not responsible for execution, latency, or performance issues stemming from the broker (currently Tradier and Tastytrade; other brokers as offered from time to time).',
        'You acknowledge that data feeds, execution pathways, and account access are managed by your broker and outside the direct control of JTNC Group, LLC.',
        'Any disconnections, trading halts, or order routing issues must be resolved directly with your brokerage firm.',
      ],
    },
    {
      heading: 'Execution Delays or Failures',
      lead: 'Automated trading is subject to technological limitations. JTNC’s Quantum Flo Auto Algo does not and cannot guarantee:',
      items: [
        'Immediate or successful order execution.',
        'Fill quality, slippage control, or priority routing.',
        'Uninterrupted service in the event of outages, connectivity issues, or broker downtime.',
        'Performance which matches other client fills and/or performance.',
      ],
    },
  ],
  closing: [
    'By continuing, you agree to all of the above.',
    'You voluntarily release JTNC Group, LLC, its affiliates, employees, and contractors from any liability for losses, damages, or system behavior resulting from the use of JTNC’s Quantum Flo Auto Algo and confirm that you are using this service at your own risk.',
  ],
};

/**
 * Shown on the disclosure-video page, with the acknowledgment checkbox at the end.
 * Rendered as bulleted lists — matching how it renders today.
 */
export const TRADING_WARNINGS = {
  sections: [
    {
      heading: 'Technology Is Fallible',
      lead: "JTNC's Quantum Flo Auto Algo uses algorithmic technology to place and manage trades on your behalf. While this automation is sophisticated and data-driven, it is not foolproof. You understand and agree that:",
      items: [
        'Algorithms can malfunction, behave unpredictably, or execute unintended trades.',
        'Real-time market conditions may evolve faster than the algorithm can respond.',
        'Software bugs, outages, or system crashes may impact trade execution or risk controls.',
        'Connectivity interruptions or broker-related delays may prevent timely exits.',
      ],
    },
    {
      heading: 'Markets Are Unpredictable',
      lead: "No algorithm, including JTNC's Quantum Flo Auto Algo, can eliminate risk in volatile or illiquid markets. By proceeding, you understand that:",
      items: [
        'Sudden price moves, news events, or liquidity gaps can result in unexpected losses.',
        'Stop-losses and other risk controls may not trigger, or may trigger at worse-than-expected prices due to slippage or gapping.',
        'Market conditions can cause losses equal to or greater than your initial capital, including total account wipeout.',
      ],
    },
    {
      heading: 'No Guarantees of Profit',
      lead: "JTNC Group, LLC does not guarantee profits of any kind. By using JTNC's Quantum Flo Auto Algo, you agree that:",
      items: [
        ['Past performance is ', { em: 'not a guarantee of future results' }, '.'],
        "You may experience losses despite the system's performance.",
        ['You understand the system is ', { em: 'not tailored' }, ' to your individual financial situation, goals, or risk tolerance.'],
      ],
    },
    {
      heading: 'You Are Solely Responsible',
      lead: "By using JTNC's Quantum Flo Auto Algo, you affirm that:",
      items: [
        'You are solely responsible for monitoring your account, trade activity, and capital.',
        'You have secured the required options trading approval from your broker.',
        ['You understand ', { em: 'how the bot works' }, ', and you are using it by your own choice.'],
        ['JTNC Group, LLC. is ', { em: 'not liable' }, " for any damages or losses, financial or otherwise, resulting from your use of JTNC's Quantum Flo Auto Algo."],
      ],
    },
    {
      heading: 'This Is Not Financial Advice',
      lead: 'JTNC Group, LLC., its owners, developers, and affiliates are not licensed financial advisors. Any statements made within our platform, newsletters, or related materials:',
      items: [
        'Are educational in nature and do not constitute financial or investment advice.',
        'Should not be interpreted as guidance specific to your individual financial needs.',
        'Should not be relied upon in place of professional advice from a licensed advisor.',
      ],
    },
    {
      heading: 'Final Acknowledgment',
      lead: 'By proceeding, you confirm the following:',
      items: [
        'I have watched the disclaimer video in its entirety.',
        'I understand that algorithmic trading carries substantial risk, including the potential loss of all invested capital.',
        'I understand that no guarantees are made, and I am solely responsible for my use of the system.',
        "I acknowledge that JTNC Group, LLC and its affiliates are not liable for any outcomes resulting from my use of JTNC's Quantum Flo Auto Algo.",
      ],
    },
    {
      heading: 'Acknowledgment of Risk and Agreement to Terms',
      lead: 'By checking the box below, you confirm the following:',
      items: [
        'I have read and fully understood the disclaimer above.',
        'I have watched the accompanying disclaimer video in its entirety.',
        'I understand and accept the risks associated with algorithmic trading.',
        // Added by the 2026-08-13 revision — the consent basis for standing authorization.
        'I understand that if I sign a Standing Trading Authorization, the system will trade my account each trading day without further daily confirmation, within the parameters I configured, until that authorization expires or I revoke it.',
        "I agree to hold JTNC Group, LLC, its affiliates, and its partners harmless for any financial losses or system behavior arising from the use of JTNC's Quantum Flo Auto Algo.",
        'I am using this software voluntarily and at my own risk.',
      ],
    },
  ],
  checkboxLabel: 'I acknowledge and agree to the terms and risks outlined above.',
  storageNote: 'Your acknowledgment will be time-stamped and securely stored as part of your user agreement.',
};
